import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { catchError, finalize, Observable, of, share, tap, throwError } from 'rxjs';
import { environment } from '../../../environments';
import { SimSubscription } from '../../interfaces/sims';
import { ApiConfigService } from '../../ssr-services/api-config/api-config.service';
import { CacheService } from '../../ssr-services/cache/cache.service';
import { SimsService } from '../sims/sims.service';

export interface CheckoutResponse {
  guestCheckoutEmail: string;
  url: string;
  stripeIntent: {
    id: string;
    amount: number;
    currency: string;
    status: string;
    client_secret: string;
  };
  iyzipayCheckoutToken: string;
  paymentId: string;
  status?: string;
  tapChargeId?: string;
  transactionId?: string;
}

export interface CreateCheckoutRequest {
  redeemPoints?: number;
  coupon?: string;
  country: string;
  esimId?: string;
  topUpSubscriptionId?: string;
  autoTopupEnabled?: boolean;
  autoChargeEnabled?: boolean;
  paymentMethodId?: string;
  default?: boolean;
  paymentToken?: string;
  paymentMethodType?: PaymentMethodType;
}

export enum PaymentMethodType {
  CARD = 'card',
  APPLE_PAY = 'applepay',
  GOOGLE_PAY = 'googlepay',
}

export interface RedeemPointsCouponRequest {
  redeemPoints?: string;
  rewardPointsApplied?: boolean;
  coupon?: string;
  couponCodeApplied?: boolean;
  country: string;
}

export interface CouponRedemptionResponse {
  couponCodeApplied: boolean;
  couponDiscountedPrice: number;
  planPricePassedForCoupon: number;
  couponCodeValue: number;
  couponCodeDiscountedValue: number;
  couponCodeToRedeem: string;
  couponCodeValueCurrency: string;
  rewardPointsApplied: boolean;
  remainingRewardPoints: number;
  updatedPlanPrice: number;
  rewardPointsToRedeem: string;
  rewardPointsValue: number;
  planActualPrice: number;
  rewardPointsValueCurrency: string;
  rewardPointsDiscountedValue: number;
  currency: string;
  finalPayableAmount: number;
  additionalPaymentRequired: boolean;
}

export interface TransactionHistoryResponse {
  _id: string;
  subscriberId: string;
  type: string;
  rewardPointsEarned: number;
  rewardPointsRefunded: number;
  rewardPointsRedeemed: number;
  currency: string;
  cashValueEarned: number;
  cashValueRefunded: number;
  cashValueRedeemed: number;
  customerId: string;
  transactionId: string;
  isVisible: boolean;
  createdAt: string;
  __v: number;
  subscriptionId: string;
}

export interface PaymentMethod {
  _id: string;
  customerId: string;
  subscriberId: string;
  paymentMethodIdStripe: string;
  default: boolean;
  paymentMethodType: string;
  paymentMethodBrand: string;
  paymentMethodLast4digits: string;
  paymentMethodExpiry: string;
  autoChargeEnabled: boolean;
  error?: string;
  errorMessage?: string;
  errorSubscriptionId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentMethodsResponse {
  paymentMethods: PaymentMethod[];
  recordCount: number;
  totalCount: number;
}

export interface UpdateSubscriptionRequest {
  autoTopupEnabled?: boolean;
  autoTopupPaymentMethodId?: string;
  autoTopupRetry?: boolean;
}

export interface AddPaymentMethodRequest {
  customerId: string;
  subscriberId: string;
}

export interface AddPaymentMethodResponse {
  stripeIntent: {
    id: string;
    clientSecret: string;
  };
  message?: string;
}

export interface ConfirmPaymentMethodRequest {
  customerId: string;
  subscriberId: string;
  paymentMethodIdStripe: string;
}

export interface ConfirmPaymentMethodResponse extends PaymentMethod {
  message?: string;
}

export interface SetDefaultPaymentMethod {
  paymentMethodId: string;
  default: boolean;
}

export interface PaymentConfirmationError {
  message: string;
}

export interface PaymentConfirmationResponse {
  status?: string;
  success?: string;
  data?: string;
  activationCode?: string;
  iccid?: string;
  transactionId?: string;
  invoiceHash?: string;
  error?: PaymentConfirmationError;
}

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  private readonly http = inject(HttpClient);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly apiConfig = inject(ApiConfigService);

  private readonly cacheService = inject(CacheService);

  private readonly simsService = inject(SimsService);

  private readonly paymentMethodsCache = signal<PaymentMethod[]>([]);

  private readonly isLoadingPaymentMethods = signal<boolean>(false);

  private lastSubscriberId: string | null = null;

  private readonly cacheInitialized = signal<boolean>(false);

  private transactionHistoryRequest: Observable<TransactionHistoryResponse> | null = null;

  public stripeIntentResponse = signal<AddPaymentMethodResponse | null>(null);

  public updatedSubscriptionAutoTopup = signal<SimSubscription | null>(null);

  public totalPaymentMethods = signal<number>(0);

  /**
   * Get payment methods signal (cached)
   * @returns Signal containing payment methods array
   */
  public paymentMethods = computed(() => this.paymentMethodsCache());

  /**
   * Get loading state signal
   * @returns Signal indicating if payment methods are being loaded
   */
  public isPaymentMethodsLoading = computed(() => this.isLoadingPaymentMethods());

  /**
   * Create checkout link for guest payment
   * @param planId - The plan ID for the checkout
   * @param email - Customer email
   * @param country - Customer country
   * @param currency - Currency code
   * @param paymentToken - Optional Tap/Stripe token when paying with card
   * @param paymentMethodType - Optional payment method type (e.g. CARD)
   * @returns Observable of guest checkout response
   */
  createGuestCheckout(
    planId: string,
    email: string,
    country: string,
    currency: string,
    paymentToken?: string,
    paymentMethodType?: PaymentMethodType
  ): Observable<CheckoutResponse> {
    const cacheContent = this.cacheService.cachedContent();
    const url = this.apiConfig.paymentMsUrl(`payments/guest/${planId}`);

    const requestBody: Record<string, unknown> = {
      email,
      country,
      currency,
      platform: environment.platform,
      customerId: cacheContent?.cache.customerId,
      socketDeviceId: this.getSocketDeviceId(),
    };
    if (paymentToken != null) requestBody['paymentToken'] = paymentToken;
    if (paymentMethodType != null) requestBody['paymentMethodType'] = paymentMethodType;

    return this.http.post<CheckoutResponse>(url, requestBody);
  }

  /**
   * Create checkout link for payment
   * @param planId - The plan ID for the checkout
   * @param payload - The checkout request payload
   * @returns Observable of checkout response
   */
  createCheckout(planId: string, payload: CreateCheckoutRequest): Observable<CheckoutResponse> {
    const url = this.apiConfig.paymentMsUrl(`payments/create/${planId}`);
    return this.http.post<CheckoutResponse>(url, { ...payload, platform: environment.platform });
  }

  /**
   * Retrieve details of a single subscription using subscriptionId
   * @param subscriptionId - The unique identifier (_id) for the subscription
   * @returns Observable of subscription details
   */
  getSubscriptionById(subscriptionId: string): Observable<SimSubscription> {
    const url = this.apiConfig.url(`subscriptions/${subscriptionId}`);
    return this.http.get<SimSubscription>(url);
  }

  /**
   * Update a subscription
   * @param subscriptionId - The unique identifier (_id) for the subscription
   * @param payload - The update request payload
   * @returns Observable of subscription details
   */
  updateSubscriptionAutoTopup(
    subscriptionId: string,
    payload: UpdateSubscriptionRequest
  ): Observable<SimSubscription> {
    const url = this.apiConfig.url(`subscriptions/${subscriptionId}`);
    return this.http.patch<SimSubscription>(url, payload).pipe(
      tap((result) => {
        this.updatedSubscriptionAutoTopup.set(result);

        // Update the subscription in the sims array
        const sims = this.simsService.sims();
        const updatedSims = sims.map((sim) => {
          const updatedSubscriptions = sim.subscriptions.map((sub) =>
            sub._id === result._id ? result : sub
          );
          return { ...sim, subscriptions: updatedSubscriptions };
        });
        this.simsService.sims.set(updatedSims);
      })
    );
  }

  /**
   * Redeem loyalty points and apply coupon for a plan
   * @param planId - The plan ID for redemption
   * @param payload - The redemption request payload
   * @returns Observable of redemption response
   */
  redeemPointsCoupon(
    planId: string,
    payload: RedeemPointsCouponRequest
  ): Observable<CouponRedemptionResponse> {
    const url = this.apiConfig.paymentMsUrl(`payments/redeem-points-coupon/${planId}`);
    return this.http.post<CouponRedemptionResponse>(url, payload);
  }

  /**
   * Get transaction history
   * @returns Observable of transaction history response
   */
  public getTransactionHistory(): Observable<TransactionHistoryResponse> {
    if (this.transactionHistoryRequest) {
      return this.transactionHistoryRequest;
    }

    this.transactionHistoryRequest = this.http
      .get<TransactionHistoryResponse>(this.apiConfig.paymentMsUrl('payments/loyalty-transactions'))
      .pipe(
        share(),
        finalize(() => {
          this.transactionHistoryRequest = null;
        })
      );
    return this.transactionHistoryRequest;
  }

  /**
   * Get payment methods for a subscriber
   * @param subscriberId - The subscriber ID
   * @returns Observable of payment methods response
   */
  public getPaymentMethods(subscriberId: string): Observable<PaymentMethodsResponse> {
    return this.http.get<PaymentMethodsResponse>(
      this.apiConfig.paymentMsUrl(`payments/${subscriberId}/methods`)
    );
  }

  /**
   * Load payment methods for a subscriber (with caching)
   * Only makes API call if subscriberId changed or cache is empty
   * @param subscriberId - The subscriber ID
   * @param forceRefresh - Force refresh even if cached
   * @returns Observable of payment methods response
   */
  public loadPaymentMethods(
    subscriberId: string,
    forceRefresh: boolean = false
  ): Observable<PaymentMethodsResponse> {
    if (this.lastSubscriberId !== null && this.lastSubscriberId !== subscriberId) {
      this.clearPaymentMethodsCache();
    }

    if (!forceRefresh && this.lastSubscriberId === subscriberId && this.cacheInitialized()) {
      return of({
        paymentMethods: this.paymentMethodsCache(),
        recordCount: this.paymentMethodsCache().length,
        totalCount: this.paymentMethodsCache().length,
      });
    }

    this.isLoadingPaymentMethods.set(true);
    this.lastSubscriberId = subscriberId;

    return this.getPaymentMethods(subscriberId).pipe(
      tap((response) => {
        this.paymentMethodsCache.set(response.paymentMethods);
        this.totalPaymentMethods.set(response.totalCount);
        this.cacheInitialized.set(true);
        this.isLoadingPaymentMethods.set(false);
      }),
      catchError((error) => {
        console.error('Error loading payment methods:', error);
        this.isLoadingPaymentMethods.set(false);

        this.cacheInitialized.set(true);
        this.totalPaymentMethods.set(0);
        return of({
          paymentMethods: [],
          recordCount: 0,
          totalCount: 0,
        });
      })
    );
  }

  public clearPaymentMethodsCache(): void {
    this.paymentMethodsCache.set([]);
    this.totalPaymentMethods.set(0);
    this.lastSubscriberId = null;
    this.cacheInitialized.set(false);
  }

  /**
   * Check if payment methods cache is initialized for current subscriber
   * @param subscriberId - The subscriber ID to check
   * @returns true if cache is initialized for this subscriber
   */
  public isPaymentMethodsCacheInitialized(subscriberId: string): boolean {
    return this.lastSubscriberId === subscriberId && this.cacheInitialized();
  }

  /**
   * Refresh payment methods for current subscriber
   */
  public refreshPaymentMethods(subscriberId: string): Observable<PaymentMethodsResponse> {
    return this.loadPaymentMethods(subscriberId, true);
  }

  /**
   * Initiate adding a new payment method
   * POST /payments/methods
   * @param payload - The add payment method request payload
   * @returns Observable of add payment method response with Stripe intent
   */
  public addNewPaymentMethod(
    payload: AddPaymentMethodRequest
  ): Observable<AddPaymentMethodResponse> {
    const url = this.apiConfig.paymentMsUrl('payments/methods');
    return this.http.post<AddPaymentMethodResponse>(url, payload).pipe(
      tap((response) => {
        this.stripeIntentResponse.set(response as AddPaymentMethodResponse);
      }),
      catchError((error) => {
        console.error('Error adding new payment method:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Confirm/complete adding a new payment method
   * PUT /payments/methods
   * @param payload - The confirm payment method request payload
   * @returns Observable of confirmed payment method
   */
  public confirmPaymentMethod(
    payload: ConfirmPaymentMethodRequest
  ): Observable<ConfirmPaymentMethodResponse> {
    const url = this.apiConfig.paymentMsUrl('payments/methods');
    return this.http.put<ConfirmPaymentMethodResponse>(url, payload).pipe(
      tap(() => {
        // Refresh payment methods cache after adding
        const subscriberId = payload.subscriberId;
        if (subscriberId) {
          this.loadPaymentMethods(subscriberId, true).subscribe();
        }
      })
    );
  }

  public setDefaultPaymentMethod(payload: SetDefaultPaymentMethod): Observable<void> {
    const url = this.apiConfig.paymentMsUrl(`payments/methods/${payload.paymentMethodId}`);
    return this.http.patch<void>(url, { default: payload.default });
  }

  public deletePaymentMethod(paymentMethodId: string): Observable<void> {
    const url = this.apiConfig.paymentMsUrl(`payments/methods/${paymentMethodId}`);
    return this.http.delete<void>(url);
  }

  /**
   * Confirm payment status for a checkout session.
   * Process payment if it is in pending state and within 15 minutes of creation.
   * Returns subscription details if payment is successful.
   * @param paymentId - Payment ID (checkout session ID)
   * @param threeDSDetails - 3D Secure authentication details (optional)
   * @param isGuest - If true, uses /payments/guest/confirmation; otherwise /payments/confirmation (logged-in)
   * @returns Observable of payment confirmation response with subscription details on success
   */
  public getPaymentConfirmation(
    paymentId: string,
    threeDSDetails?: string,
    isGuest?: boolean
  ): Observable<PaymentConfirmationResponse> {
    const params: { paymentId: string; threeDSDetails?: string; customerId?: string } = {
      paymentId,
    };
    if (threeDSDetails != null && threeDSDetails !== '') {
      params.threeDSDetails = threeDSDetails;
    }
    if (isGuest) {
      const customerId = this.cacheService.cachedContent()?.cache.customerId;
      if (customerId) params.customerId = customerId;
    }
    const path = isGuest ? 'payments/guest/confirmation' : 'payments/confirmation';
    const url = this.apiConfig.paymentMsUrl(path);
    return this.http.get<PaymentConfirmationResponse>(url, { params });
  }

  private getSocketDeviceId(): string | undefined {
    if (!isPlatformBrowser(this.platformId)) return undefined;

    const key = 'socketDeviceId';
    const existing = globalThis.localStorage.getItem(key);
    if (existing) return existing;

    const generated = globalThis.crypto.randomUUID();
    globalThis.localStorage.setItem(key, generated);
    return generated;
  }
}
